import * as React from "react";

import {
  SaveFormFileValues,
  SaveFormMediaValues,
  SaveFormValues,
} from "~/components/SaveForm/shared";
import { Collection, Save, Space, Tag } from "~/stores/space/store";

type ModalActions = {
  openAddSpace: () => void;
  openEditSpace: (params: { spaceId: string }) => void;
  openAddCollection: (params?: { spaceId: string }) => void;
  openEditCollection: (params: {
    collection: Collection;
    space: Space;
  }) => void;
  openAddTag: (params?: { spaceId: string }) => void;
  openEditTag: (params: { tag: Tag; space: Space }) => void;
  openAddSave: (
    params: { spaceId?: string } & (
      | { type: "URL" }
      | { type: "FILE"; fileMeta: SaveFormFileValues["fileMeta"] }
      | { type: "MEDIA"; mediaMeta: SaveFormMediaValues["mediaMeta"] }
      | { type: "NOTE" }
    ),
  ) => void;
  openEditSave: (
    params: Save & { spaceId: string; step?: SaveFormValues["step"] },
  ) => void;
  // openEditSave: (params: Save) => void;
  openCopySave: (params: { save: Save; srcSpaceId: string }) => void;
  openMoveSave: (params: { save: Save; srcSpaceId: string }) => void;
  openCreateTeam: (params: { onSuccess: (slug: string) => void }) => void;
};

export type ModalType = GetModelTypesFromActionsContext<ModalActions>;

export type ModalStack = Map<string, ModalType>;

export type ModalStackAction =
  | { type: "ADD_MODAL"; key: string; value: ModalType }
  | { type: "REMOVE_MODAL"; key: string };

export type AccountActionsContext = ModalActions & {
  /**
   * This is hacky/cool(?) way to pass unserializable state around routes/dialogs/you name it\
   * E.g - For Media, we open media selector as soon as user clicks, then we immediately set this state in the action handler, and _then_ we open the dialog/route and by the time route/dialog opens that unserializable entity is made available
   * Think global vars
   */
  getTempState<T extends ModalType["type"]>(): Extract<
    ModalType,
    { type: T }
  >["params"];
  removeModalByType: (type: ModalType["type"]) => void;
};

export const AccountActionsContext =
  React.createContext<AccountActionsContext | null>(null);

export function useAccountActions() {
  const ctx = React.useContext(AccountActionsContext);
  if (ctx === null) {
    throw new Error(
      "useAccountActions must be used within a AccountActionsProvider",
    );
  }
  return ctx;
}
type GetModelTypesFromActionsContext<T extends ModalActions> = {
  [K in keyof T & string]: T[K] extends (params: infer P) => void
    ? { type: K extends `open${infer M}` ? Uncapitalize<M> : never; params: P }
    : never;
}[keyof T & string];
